import React, { useState } from 'react'
import Select from 'react-select'

import { Collapse, List, ListItem, ListItemButton } from '@mui/material'
import { bemJoin } from 'bem-join'
import arrowRight from 'component-library-next/build/images/arrow-right.svg'
import cloneDeep from 'lodash.clonedeep'
import { Transforms } from 'slate'

import { IntlMessageKeys, useIntl } from '@app/intl/Intl'
import { useTemplatedForm } from '@app/lib/context/TemplatedForm'
import { Heading } from '@app/lib/typography/Heading/Heading'
import { sortTemplateCollections } from '@app/modules/Messages/Components/helpers/sortTemplateCollections'
import {
  TemplateCollectionsQuery,
  useTemplateCollectionsQuery
} from '@app/queries/TemplateCollections/TemplateCollections.queries'

import './TemplatePicker.scss'

export type Template = TemplateCollectionsQuery['templateCollections']['items'][number]['templates']
export type Field = Exclude<Template, null | undefined>[number]['fields'][number]

export const TemplatePicker = () => {
  const ctx = useTemplatedForm()
  const { formatMessage } = useIntl()
  const b = bemJoin('template-picker')
  const [listOpen, setListOpen] = useState<boolean>(false)
  const [openSubList, setOpenSubList] = useState('')

  const { data } = useTemplateCollectionsQuery({
    variables: {
      filters: {
        uid: {
          like: 'Lawyer'
        }
      }
    }
  })

  const templateCollections = data?.templateCollections

  if (!templateCollections) {
    return null
  }

  const sortedCollections = sortTemplateCollections(templateCollections)

  const handleSubListClick = (uid: string) => {
    if (openSubList === uid) {
      setOpenSubList('')
      return
    }

    setOpenSubList(uid)
  }

  const appendTemplate = (fields: Field[]) => {
    fields.forEach((field) => {
      const editor = ctx?.editors.get(field.name)
      if (editor) {
        editor.children.map(() => {
          Transforms.delete(editor, { at: [0] })
        })
        field.text.map((node) => {
          Transforms.insertNodes(editor, cloneDeep(node))
        })
      }
    })

    setOpenSubList('')
    setListOpen(false)
  }

  return (
    <>
      <h3>Textvorlage nutzen</h3>
      <div className={b('wrapper')}>
        <div onClick={() => setListOpen(!listOpen)}>
          <Select
            isDisabled={true}
            menuIsOpen={false}
            isSearchable={false}
            styles={{ control: (styles) => ({ ...styles, backgroundColor: 'white' }) }}
            placeholder={formatMessage({ id: 'TemplateEditor.Toggle' })}
            className={b('toggle')}
          />
        </div>
        <Collapse in={listOpen} timeout='auto' unmountOnExit>
          <List className={b('list')}>
            {sortedCollections.map((collection) => {
              return (
                <div key={collection.title}>
                  <ListItemButton
                    className={b('list-item')}
                    onClick={() => handleSubListClick(collection.uid)}
                  >
                    <div className={b('list-item-name')}>
                      <img
                        src={arrowRight}
                        className={b('arrow', {
                          open: openSubList === collection.uid
                        })}
                        alt={'arrow'}
                      />
                      <Heading color={'primary'} size={'sm'}>
                        {formatMessage({ id: collection.uid as IntlMessageKeys })}
                      </Heading>
                    </div>
                    <div className={b('counter')}>{collection.templates.length}</div>
                  </ListItemButton>
                  <Collapse in={openSubList === collection.uid}>
                    <List className={b('sub-list')}>
                      {collection.templates.map((template) => {
                        return (
                          <ListItem
                            className={b('sub-list-element')}
                            onClick={() => {
                              appendTemplate(template.fields)
                            }}
                            key={template.uid}
                          >
                            {template.title}
                          </ListItem>
                        )
                      })}
                    </List>
                  </Collapse>
                </div>
              )
            })}
          </List>
        </Collapse>
      </div>
    </>
  )
}
